// TMP Tool — list of Traffic Management Plans + create dialog. Supports ?project= auto-open.
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Cone, X, Loader2, FileText, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import Pill from "@/components/Pill";
import AdminDeleteButton from "@/components/AdminDeleteButton";
import { useAssignRecipients, AssignRecipientsStep } from "@/components/AssignRecipientsStep";

const CAN_EDIT = new Set(["director", "administrator", "consultant"]);
const STATUS_TONE = { Draft: "neutral", "Under Review": "amber", Approved: "green", Issued: "blue", Archived: "neutral" };

export default function TmpListPage() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const { user } = useAuth();
    const { isInternalView } = useView();
    const canEdit = CAN_EDIT.has(user?.role) && isInternalView;

    const [records, setRecords] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/tmp");
            setRecords(data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load TMPs");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!canEdit) return;
        api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
    }, [canEdit]);

    // Deep-link from Compliance Hub: ?project=<id>&client=<id> -> open or create project-linked TMP
    useEffect(() => {
        const projectId = params.get("project");
        if (!projectId) return;
        const back = `?back=${encodeURIComponent("/app/projects/" + projectId)}`;
        (async () => {
            try {
                const { data } = await api.get(`/tmp?project_id=${projectId}`);
                if (data && data.length) { navigate(`/app/tmp/${data[0].id}${back}`, { replace: true }); return; }
                const clientId = params.get("client");
                if (canEdit && clientId) {
                    const { data: rec } = await api.post("/tmp", { client_id: clientId, project_id: projectId });
                    navigate(`/app/tmp/${rec.id}${back}`, { replace: true });
                } else { setParams({}, { replace: true }); }
            } catch (e) { setParams({}, { replace: true }); }
        })();
    }, [params, canEdit, navigate, setParams]);

    return (
        <div className="p-6 max-w-[1200px] mx-auto" data-testid="tmp-list-page">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <div className="flex items-center gap-2.5">
                        <span className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: "#FBEFE5", color: "#C75A1B" }}>
                            <Cone className="w-5 h-5" />
                        </span>
                        <h1 className="text-2xl font-serif text-[#1F1F1F]">Traffic Management Plans</h1>
                    </div>
                    <p className="text-sm text-[#6F6A64] mt-1.5 ml-0.5">Capture site traffic management online and export a professional Word or PDF plan.</p>
                </div>
                {canEdit && (
                    <button data-testid="tmp-create-button" onClick={() => setShowCreate(true)}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-transform hover:scale-[1.02]"
                        style={{ background: "#C75A1B" }}><Plus className="w-4 h-4" /> New TMP</button>
                )}
            </div>

            {loading ? (
                <div className="grid place-items-center py-24 text-[#6F6A64]"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : records.length === 0 ? (
                <div className="border border-dashed rounded-2xl py-20 text-center" style={{ borderColor: "#E5E0DA" }} data-testid="tmp-empty-state">
                    <Cone className="w-10 h-10 mx-auto text-[#C9C2B8]" />
                    <p className="mt-3 text-[#1F1F1F] font-semibold">No traffic management plans yet</p>
                    <p className="text-sm text-[#6F6A64] mt-1">Create your first TMP — default wording is pre-filled and fully editable.</p>
                    {canEdit && <button onClick={() => setShowCreate(true)} data-testid="tmp-empty-create"
                        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: "#C75A1B" }}>
                        <Plus className="w-4 h-4" /> New TMP</button>}
                </div>
            ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="tmp-grid">
                    {records.map((r) => (
                        <div key={r.id} className="relative">
                            <button data-testid={`tmp-card-${r.id}`} onClick={() => navigate(`/app/tmp/${r.id}`)}
                                className="w-full text-left bg-white border rounded-2xl p-4 hover:shadow-brand transition-shadow group" style={{ borderColor: "#E5E0DA" }}>
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-mono text-[#9A958C]">{r.reference}</span>
                                    <Pill tone={STATUS_TONE[r.status] || "neutral"}>{r.status}</Pill>
                                </div>
                                <h3 className="mt-2 font-semibold text-[#1F1F1F] leading-snug line-clamp-2">{r.project_name || "Untitled TMP"}</h3>
                                <div className="mt-2 text-xs text-[#6F6A64] space-y-0.5">
                                    {r.project_number && <div>{r.project_number}</div>}
                                    <div>{(r.sections || []).length} sections · v{r.version}</div>
                                </div>
                                <div className="mt-3 flex items-center gap-1 text-[12px] font-semibold" style={{ color: "#C75A1B" }}>
                                    Open <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                                </div>
                            </button>
                            <div className="absolute top-2 right-2">
                                <AdminDeleteButton endpoint={`/tmp/${r.id}`} label="TMP"
                                    testId={`tmp-delete-${r.id}`} onDeleted={load} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showCreate && <CreateDialog clients={clients} onClose={() => setShowCreate(false)}
                onCreated={(rec) => { setShowCreate(false); navigate(`/app/tmp/${rec.id}`); }} />}
        </div>
    );
}

function CreateDialog({ clients, onClose, onCreated }) {
    const [clientId, setClientId] = useState("");
    const [projectId, setProjectId] = useState("");
    const [projectName, setProjectName] = useState("");
    const [projects, setProjects] = useState([]);
    const [saving, setSaving] = useState(false);
    const assign = useAssignRecipients(clientId);

    useEffect(() => {
        if (!clientId) { setProjects([]); return; }
        api.get(`/projects?client_id=${clientId}`).then(({ data }) => setProjects(data || [])).catch(() => {});
    }, [clientId]);

    const submit = async () => {
        if (!clientId) { toast.error("Select a client"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/tmp", {
                client_id: clientId, project_id: projectId || null, project_name: projectName || null,
                assigned_recipients: assign.recipients.length ? assign.recipients : undefined,
                assigned_message: assign.message.trim() || undefined,
            });
            toast.success("TMP created");
            onCreated(data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed to create"); }
        finally { setSaving(false); }
    };

    const field = "w-full border rounded-lg px-3 py-2 text-sm mt-0.5";
    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tmp-create-dialog">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-serif text-lg">New Traffic Management Plan</h3>
                    <button onClick={onClose}><X className="w-5 h-5 text-[#6F6A64]" /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-semibold text-[#6F6A64]">Client *</label>
                        <select data-testid="tmp-create-client" className={field} value={clientId} onChange={(e) => setClientId(e.target.value)} style={{ borderColor: "#E5E0DA" }}>
                            <option value="">Select client…</option>
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-[#6F6A64]">Project</label>
                        <select data-testid="tmp-create-project" className={field} value={projectId} onChange={(e) => setProjectId(e.target.value)} style={{ borderColor: "#E5E0DA" }}>
                            <option value="">None</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.reference} · {p.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-[#6F6A64]">Project name</label>
                        <input data-testid="tmp-create-name" className={field} value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="e.g. Maple St demolition" style={{ borderColor: "#E5E0DA" }} />
                    </div>
                    <AssignRecipientsStep state={assign} testIdPrefix="tmp-create-assign" />
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-[#6F6A64]">Cancel</button>
                    <button onClick={submit} disabled={saving} data-testid="tmp-create-submit"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60" style={{ background: "#C75A1B" }}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Create
                    </button>
                </div>
            </div>
        </div>
    );
}
